import React, { useMemo, useState } from "react";
import { sourceNeedsAttachment } from "../../utils/trackerBridge";

function formatBytes(bytes) {
  const size = Number(bytes || 0);
  if (!Number.isFinite(size) || size <= 0) return "0 B";

  const units = ["B", "KB", "MB", "GB"];
  let value = size;
  let index = 0;

  while (value >= 1024 && index < units.length - 1) {
    value /= 1024;
    index += 1;
  }

  return `${value.toFixed(index === 0 ? 0 : 2)} ${units[index]}`;
}

function isExpired(urlExpiresAt) {
  if (!urlExpiresAt) return false;
  const timestamp = new Date(urlExpiresAt).getTime();
  if (Number.isNaN(timestamp)) return false;
  return timestamp < Date.now();
}

const TrackerAttachmentUploader = ({
  source,
  ticketNumber,
  attachments,
  uploading,
  onUpload,
  onDelete,
  onRefresh,
  readOnly = false,
}) => {
  const [pendingFiles, setPendingFiles] = useState([]);

  const required = useMemo(() => sourceNeedsAttachment(source), [source]);

  const handleFileChange = (event) => {
    setPendingFiles(Array.from(event.target.files || []));
  };

  const handleUpload = async () => {
    if (pendingFiles.length === 0) return;
    try {
      await onUpload(pendingFiles, ticketNumber);
      setPendingFiles([]);
    } catch (error) {
      // toast handled by caller/context
    }
  };

  return (
    <section className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h4 className="text-sm font-semibold text-slate-900">Attachments</h4>
          <p className="text-xs text-slate-600">
            {readOnly
              ? "View uploaded files. Signed URL refresh is supported."
              : "Upload multiple files (PDF, image, Office docs). Signed URL refresh is supported."}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {readOnly ? (
            <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-semibold text-slate-700">
              View Only
            </span>
          ) : required ? (
            <span className="rounded-full bg-rose-100 px-2 py-0.5 text-[11px] font-semibold text-rose-700">
              Required for Email/Viber
            </span>
          ) : (
            <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-semibold text-slate-700">
              Optional
            </span>
          )}
        </div>
      </div>

      {!readOnly ? (
        <div className="mt-3 grid gap-2 sm:grid-cols-[1fr_auto]">
          <input
            type="file"
            multiple
            onChange={handleFileChange}
            className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-700"
          />
          <button
            type="button"
            onClick={handleUpload}
            disabled={uploading || pendingFiles.length === 0 || !ticketNumber}
            className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-70"
          >
            {uploading ? "Uploading..." : `Upload ${pendingFiles.length ? `(${pendingFiles.length})` : ""}`}
          </button>
        </div>
      ) : null}

      {!readOnly && !ticketNumber ? (
        <p className="mt-2 text-xs text-amber-700">Create or load a ticket first before uploading attachments.</p>
      ) : null}

      <div className="mt-4 space-y-2">
        {attachments?.length ? (
          attachments.map((attachment) => {
            const expired = isExpired(attachment.url_expires_at);

            return (
              <div
                key={attachment.upload_id}
                className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2"
              >
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium text-slate-900">
                    {attachment.original_file_name || attachment.file_name}
                  </p>
                  <p className="text-xs text-slate-600">
                    {formatBytes(attachment.file_size)} | Uploaded: {attachment.created_at || "-"}
                  </p>
                </div>

                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={async () => {
                      if (!attachment.signed_url || expired) {
                        try {
                          await onRefresh(attachment.upload_id);
                        } catch (error) {
                          // toast handled by caller/context
                        }
                        return;
                      }
                      window.open(attachment.signed_url, "_blank", "noopener,noreferrer");
                    }}
                    className="rounded-md border border-slate-300 px-2 py-1 text-xs font-medium text-slate-700 hover:bg-slate-100"
                  >
                    {expired ? "Refresh & Open" : "Open"}
                  </button>
                  <button
                    type="button"
                    onClick={async () => {
                      try {
                        await onRefresh(attachment.upload_id);
                      } catch (error) {
                        // toast handled by caller/context
                      }
                    }}
                    className="rounded-md border border-blue-300 px-2 py-1 text-xs font-medium text-blue-700 hover:bg-blue-50"
                  >
                    Refresh URL
                  </button>
                  {!readOnly ? (
                    <button
                      type="button"
                      onClick={async () => {
                        try {
                          await onDelete(attachment.upload_id);
                        } catch (error) {
                          // toast handled by caller/context
                        }
                      }}
                      className="rounded-md border border-rose-300 px-2 py-1 text-xs font-medium text-rose-700 hover:bg-rose-50"
                    >
                      Delete
                    </button>
                  ) : null}
                </div>
              </div>
            );
          })
        ) : (
          <p className="rounded-lg border border-dashed border-slate-300 bg-slate-50 px-3 py-3 text-xs text-slate-600">
            No attachments uploaded yet.
          </p>
        )}
      </div>
    </section>
  );
};

export default TrackerAttachmentUploader;
